import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { PureDataComponent } from '@aytm/lib/common/components/pure-component'
import { is, List, Map, Iterable } from 'immutable'
import { NBSP } from '@aytm/lib/common/const'
import { stopEvent, touchMapper } from '@aytm/lib/common/func'

import '@aytm/lib/rfd-ui/styles/advanced/slider.scss'

export default class Slider extends PureDataComponent {
  static propTypes = {
    value: PropTypes.number,
    positions: PropTypes.object.isRequired,
    minPosition: PropTypes.number,
    maxPosition: PropTypes.number,
    minLabelLeft: PropTypes.number,
    maxLabelLeft: PropTypes.number,
    fillFromPosition: PropTypes.number,
    maxFillPosition: PropTypes.number,
    defaultX: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showTicks: PropTypes.bool,
    showLabel: PropTypes.bool,
    showLabelDragging: PropTypes.bool,
    showBorderTexts: PropTypes.bool,
    showFill: PropTypes.bool,
    showRange: PropTypes.bool,
    snapMove: PropTypes.bool,
    snapCenter: PropTypes.bool,
    snapPoles: PropTypes.bool,
    staticLabel: PropTypes.bool,
    width: PropTypes.number,
    labelPlaceholder: PropTypes.string,
    theme: PropTypes.string,
    onChange: PropTypes.func,
    onSubmit: PropTypes.func
  }

  constructor(props) {
    super(props)
    this.state = {
      handleX: null,
      dragging: false,
      snapped: false,
      positions: null,
      snapToPosition: null
    }
    this.mouseDownHandler = null
  }

  addGlobalEvents() {
    const that = this

    const finalizeDrag = () => {
      that.setState({ dragging: false })
      that.snapHandler()
      const { onSubmit } = this.props
      const { positions, snapToPosition } = this.state
      if ('function' == typeof onSubmit && positions && snapToPosition)
        onSubmit(positions.indexOf(snapToPosition), snapToPosition)
    }
    const mouseUpHandler = event => {
      stopEvent(event)
      that.removeGlobalEvents()
      finalizeDrag()
      return false
    }
    const mouseMoveHandler = event => {
      if (0 === event.which) {
        that.removeGlobalEvents()
        finalizeDrag()
      } else if (that.state.dragging) {
        that.currentMouseX = event.clientX
        if (!that.timeOutId) {
          that.timeOutId = setTimeout(() => {
            that.updateHandleOnMouse(that.currentMouseX)
            clearTimeout(that.timeOutId)
            that.timeOutId = null
          }, 0)
        }
        if (-1 == that.renderedMouseX) {
          that.renderedMouseX = event.clientX
          that.updateHandleOnMouse(event.clientX)
        }
      }
    }

    if (null == this.parentDocument) {
      this.parentDocument = this.refRoot?.ownerDocument || document
    }
    if ('function' == typeof this.removeGlobalEvents) this.removeGlobalEvents()
    this.removeGlobalEvents = () => {
      this.parentDocument.removeEventListener('mouseup', mouseUpHandler, true)
      this.parentDocument.removeEventListener('mousemove', mouseMoveHandler, true)
      this.parentDocument.removeEventListener('touchend', touchMapper, true)
      this.parentDocument.removeEventListener('touchcancel', touchMapper, true)
      this.parentDocument.removeEventListener('touchmove', touchMapper, true)
      that.removeGlobalEvents = null
    }

    this.parentDocument.addEventListener('mouseup', mouseUpHandler, true)
    this.parentDocument.addEventListener('touchend', touchMapper, true)
    this.parentDocument.addEventListener('touchcancel', touchMapper, true)
    this.parentDocument.addEventListener('mousemove', mouseMoveHandler, true)
    this.parentDocument.addEventListener('touchmove', touchMapper, true)
  }

  mouseDown() {
    return (
      this.mouseDownHandler ||
      (this.mouseDownHandler = event => {
        if (0 === event.button) {
          stopEvent(event)
          this.setState({ dragging: true, snapped: false })
          this.updateHandleOnMouse(event.clientX)
          this.addGlobalEvents()
          return false
        }
        return true
      })
    )
  }

  updateHandleOnMouse(mouseX) {
    if (this.refRoot) {
      const { positions, snapToPosition } = this.state
      const { maxFillPosition } = this.props
      const stateUpdate = {}
      let doStateUpdate = false
      const sliderRect = this.refRoot.getBoundingClientRect()
      const relMouseX = Math.min(sliderRect.right, Math.max(sliderRect.left, mouseX)) - sliderRect.left
      const handleX = positions
        ? (() => {
            const minPositionIndex = parseInt(this.props.minPosition, 10)
            const maxPositionIndex = parseInt(this.props.maxPosition, 10)
            const minPosition = isNaN(minPositionIndex) ? positions.first() : positions.get(minPositionIndex)
            const maxPosition = isNaN(maxPositionIndex) ? positions.last() : positions.get(maxPositionIndex)
            return Math.min(
              Math.max(relMouseX, minPosition ? minPosition.get('x') : 0),
              maxPosition ? maxPosition.get('x') : 0
            )
          })()
        : relMouseX
      if (handleX != this.state.handleX) {
        stateUpdate.handleX = handleX
        doStateUpdate = true
      }
      if (positions && (handleX != this.state.handleX || !snapToPosition)) {
        // find closest position
        const { onChange, snapMove } = this.props
        const newSnapToPosition = positions.find(p => handleX >= p.get('l') && handleX <= p.get('r'))
        if (!newSnapToPosition) return
        if (maxFillPosition != null && newSnapToPosition.getIn(['data', 'i']) > maxFillPosition) return
        if (Boolean(snapMove) && newSnapToPosition) stateUpdate.handleX = newSnapToPosition.get('x')
        if (!is(newSnapToPosition, snapToPosition)) {
          stateUpdate.snapToPosition = newSnapToPosition
          doStateUpdate = true
          if ('function' === typeof onChange) onChange(positions.indexOf(newSnapToPosition), newSnapToPosition)
        }
      }
      if (doStateUpdate) this.setState(stateUpdate)
    }
  }

  recalcPositions(positions = this.props.positions) {
    if (this.refRoot) {
      const sliderRect = this.refRoot.getBoundingClientRect()
      const sliderWidth = sliderRect.right - sliderRect.left || this.props.width
      const pos = Iterable.isIterable(positions) ? positions : List(positions)
      const posAmount = positions.count()
      const sectionWidth = sliderWidth / (posAmount - 1)
      const halfSectionWidth = sectionWidth / 2
      const newPositions = pos.map((p, i) =>
        Map({
          id: p.get('id'), // position id
          text: p.get('text'), // position text
          data: p.delete('id').delete('text'), // additional data
          l: Math.max(i * sectionWidth - halfSectionWidth, 0), // left border of snap zone
          r: Math.min(i * sectionWidth + halfSectionWidth, sliderWidth), // right border of snap zone
          x: i * sectionWidth, // snap position
          w: sectionWidth // section width
        })
      )
      this.setState({ positions: newPositions })
    }
  }

  snapHandler() {
    const { onChange } = this.props
    const { positions, snapToPosition, handleX } = this.state
    if (snapToPosition) {
      const snap = (() => {
        const { width, snapCenter, snapPoles } = this.props
        const fillFromPosition = parseInt(this.props.fillFromPosition, 10) || 0
        const dx = (3 * width) / 100
        if (fillFromPosition > 0) {
          if (snapCenter) {
            const cp = positions.get(fillFromPosition)
            if (handleX >= cp.get('l') - dx && handleX <= cp.get('r') + dx) return cp
          }
          if (snapPoles) {
            const lp = positions.first()
            if (handleX >= lp.get('l') - dx && handleX <= lp.get('r') + dx) return lp
            const rp = positions.last()
            if (handleX >= rp.get('l') - dx && handleX <= rp.get('r') + dx) return rp
          }
        }
        return snapToPosition
      })()
      const snapX = snap.get('x')

      if (snapToPosition != snap) {
        this.setState({ snapToPosition: snap, snapped: true })
        if ('function' == typeof onChange) onChange(positions.indexOf(snap), snap)
      }

      if (handleX != snapX) this.setState({ handleX: snapX })
    }
  }

  getPositionByValue() {
    const { value } = this.props
    const { positions } = this.state
    return null != value && positions ? positions.get(value) : null
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      !this.state.dragging &&
      // nextProps.positions != this.props.positions ||
      // (nextProps.positions && !this.state.positions) ||
      nextProps.width != this.props.width
    ) {
      setTimeout(() => {
        this.recalcPositions()
        this.snapHandler()
      }, 0)
    }
  }

  componentDidMount() {
    super.componentDidMount()
    if (this.refRoot) {
      this.refRoot.addEventListener('mousedown', this.mouseDown(), true)
      this.refRoot.addEventListener('touchstart', touchMapper, { passive: true })
    }
    if (this.props.positions && !this.state.positions) this.recalcPositions(this.props.positions)
  }

  componentWillUnmount() {
    super.componentWillUnmount()
    if ('function' == typeof this.removeGlobalEvents) this.removeGlobalEvents()
    if (this.refRoot) {
      this.refRoot.removeEventListener('mousedown', this.mouseDown(), true)
      this.refRoot.removeEventListener('touchstart', touchMapper, true)
    }
  }

  componentDidUpdate() {
    const { minLabelLeft, maxLabelLeft, value } = this.props
    const label = this.refLabel
    const blabel = this.refBLabel
    const arrow = this.refArrow
    const slider = this.refRoot
    const ltext = this.refLText
    const rtext = this.refRText
    const labelarr = this.refLabelArr
    const showLabel = null == this.props.showLabel ? true : Boolean(this.props.showLabel)
    const staticLabel = null == this.props.staticLabel ? true : Boolean(this.props.staticLabel)
    const showLabelDragging = null == this.props.showLabelDragging ? showLabel : Boolean(this.props.showLabelDragging)
    const showBorderTexts =
      (null == value || this.state.dragging) &&
      this.props.showBorderTexts &&
      this.state.positions &&
      this.state.positions.count() > 1
    const sliderRect = slider.getBoundingClientRect()
    const sliderWidth = sliderRect.width
    const arrowRect = arrow.getBoundingClientRect()
    const arrowMid = arrowRect.left + arrowRect.width / 2 - sliderRect.left

    if ((showLabel || showLabelDragging) && label && arrow && slider) {
      const labelRect = label.getBoundingClientRect()
      const labelWidth = labelRect.width
      const labelMid = arrowMid - labelWidth / 2
      const labelLeft = staticLabel ? labelMid : Math.min(Math.max(labelMid, 0), sliderWidth - labelWidth)
      const labelStrictLeft = minLabelLeft != undefined && labelLeft < minLabelLeft ? minLabelLeft : labelLeft
      const labelStrictRight =
        maxLabelLeft != undefined && sliderWidth - labelWidth < labelStrictLeft
          ? sliderWidth - labelWidth - maxLabelLeft
          : labelStrictLeft
      label.style.left = `${labelStrictRight}px`
    }

    if (showLabelDragging && this.state.dragging && labelarr && arrow && slider) {
      const labelArrLeft = Math.min(Math.max(arrowMid, 10), sliderWidth - 10)
      labelarr.style.left = `${labelArrLeft}px`
    }

    if (showBorderTexts && ltext) {
      // const lTextRect = ltext.getBoundingClientRect()
      ltext.style.visibility = value != null ? 'hidden' : 'visible'
    }
    if (showBorderTexts && rtext) {
      // const rTextRect = rtext.getBoundingClientRect()
      rtext.style.visibility = value != null ? 'hidden' : 'visible'
    }

    if ((showLabel || showLabelDragging) && blabel && arrow && slider) {
      const blabelRect = blabel.getBoundingClientRect()
      const blabelWidth = blabelRect.width
      const blabelMid = arrowMid - blabelWidth / 2
      const blabelLeft = staticLabel ? blabelMid : Math.min(Math.max(blabelMid, 0), sliderWidth - blabelWidth)
      blabel.style.left = `${blabelLeft}px`
    }
  }

  //TODO: consider breaking into subrenders
  render() {
    const { positions, dragging, snapped, snapToPosition } = this.state
    const { showTicks, showFill, defaultX, value, labelPlaceholder, width, theme } = this.props
    const fillFromPosition = parseInt(this.props.fillFromPosition, 10) || 0
    const maxFillPosition = parseInt(this.props.maxFillPosition, 10) || 0
    const showLabel = null == this.props.showLabel ? true : Boolean(this.props.showLabel)
    const showLabelDragging = null == this.props.showLabelDragging ? showLabel : Boolean(this.props.showLabelDragging)
    const showBorderTexts =
      (null == value || dragging) && this.props.showBorderTexts && positions && positions.count() > 1
    const { handleX, labelText, bottomText } = (() => {
      if (dragging || snapped) {
        const snapToPositionText = snapToPosition && snapToPosition.get('text')
        const snapToPositionData = snapToPosition && snapToPosition.get('data')
        return {
          handleX: this.state.handleX,
          labelText: snapToPositionText ? snapToPositionText : labelPlaceholder,
          bottomText:
            snapToPositionData && snapToPositionData.has('bottomText') ? snapToPositionData.get('bottomText') : ''
        }
      } else {
        const selectedPosition = this.getPositionByValue(value)
        const selectedPositionX = selectedPosition && selectedPosition.get('x')
        const selectedPositionText = selectedPosition && selectedPosition.get('text')
        const selectedPositionData = selectedPosition && selectedPosition.get('data')
        return {
          handleX: selectedPositionX != null ? selectedPositionX : defaultX,
          labelText: selectedPositionText ? selectedPositionText : positions ? labelPlaceholder : '',
          bottomText:
            selectedPositionData && selectedPositionData.has('bottomText') ? selectedPositionData.get('bottomText') : ''
        }
      }
    })()
    const hasValue = null != value || dragging
    const { minPosition, maxPosition, showRange } = (() => {
      const minPositionIndex = parseInt(this.props.minPosition, 10)
      const maxPositionIndex = parseInt(this.props.maxPosition, 10)
      const minPosition = isNaN(minPositionIndex)
        ? positions
          ? positions.first()
          : null
        : positions
        ? positions.get(minPositionIndex)
        : null
      const maxPosition = isNaN(maxPositionIndex)
        ? positions
          ? positions.last()
          : null
        : positions
        ? positions.get(maxPositionIndex)
        : null
      return {
        minPosition: minPosition,
        maxPosition: maxPosition,
        showRange:
          null == this.props.showRange
            ? minPosition && maxPosition && (minPosition != positions.first() || maxPosition != positions.last())
            : this.props.showRange
      }
    })()
    const fillFromX =
      (positions ? (positions.has(fillFromPosition) ? positions.getIn([fillFromPosition, 'x']) : 0) : 0) || 0
    const fillEnableFromX =
      (positions ? (positions.has(maxFillPosition) ? positions.getIn([maxFillPosition, 'x']) : 0) : 0) || 0

    const { fillLeft, fillWidth } = showFill
      ? (() => {
          const fillToX = hasValue ? handleX : fillFromX
          return {
            fillLeft: (fillFromX < fillToX ? fillFromX : fillToX) || 0,
            fillWidth: (fillFromX < fillToX ? fillToX - fillFromX : fillFromX - fillToX) || 0
          }
        })()
      : { fillLeft: null, fillWidth: null }

    return (
      <div
        className={classNames('rfd-ui-slider', {
          'rfd-ui-slider-dragging': dragging,
          [`rfd-ui-slider-${theme}`]: theme
        })}
        ref={this.saveRef('Root')}
        style={{ width: width }}
        data-testid="SW-Slider"
      >
        <div className="rfd-ui-slider-label">
          {showLabelDragging && dragging && (
            <span className="rfd-ui-slider-label-arrow" ref={this.saveRef('LabelArr')} />
          )}
          {(showLabel || (showLabelDragging && dragging)) && labelText && (
            <div
              ref={this.saveRef('Label')}
              className={classNames('rfd-ui-slider-label-text', {
                'rfd-ui-slider-label-text-novalue': !hasValue
              })}
              dangerouslySetInnerHTML={{ __html: labelText || NBSP }}
            />
          )}
        </div>
        <div className="rfd-ui-slider-outer-panel">
          <div className="rfd-ui-slider-panel">
            {showRange && (
              <div
                className="rfd-ui-slider-range-fill"
                style={{
                  left: (minPosition ? minPosition.get('x') : 0) || 0,
                  width: (maxPosition ? maxPosition.get('x') - (minPosition ? minPosition.get('x') : 0) : 0) || 0
                }}
              />
            )}
            {fillEnableFromX > 0 && (
              <span
                ref={this.saveRef('Enabled')}
                className="rfd-ui-slider-enabled-fill"
                style={{ left: 0, width: fillEnableFromX }}
              />
            )}
            {showFill && <div className="rfd-ui-slider-fill" style={{ left: fillLeft, width: fillWidth }} />}
            {fillFromX > 0 && <span className="rfd-ui-slider-fill-point" style={{ left: fillFromX }} />}
            {showBorderTexts && positions && (
              <div
                className="rfd-ui-slider-left-text"
                key="tl"
                ref={this.saveRef('LText')}
                dangerouslySetInnerHTML={{ __html: positions.first().get('text') }}
              />
            )}
            {showBorderTexts && positions && (
              <div
                className="rfd-ui-slider-right-text"
                key="tr"
                ref={this.saveRef('RText')}
                dangerouslySetInnerHTML={{ __html: positions.last().get('text') }}
              />
            )}
            {showTicks &&
              positions &&
              positions
                .slice(1, -1)
                .map((p, pi) => {
                  const tickX = p.get('x')
                  const tickFill =
                    null != fillLeft && null != fillWidth ? fillLeft < tickX && tickX < fillLeft + fillWidth : false
                  return (
                    <div
                      className={classNames('rfd-ui-slider-tick', {
                        'rfd-ui-slider-tick-fill': tickFill,
                        'rfd-ui-slider-tick-bg': !tickFill
                      })}
                      style={{ left: tickX }}
                      key={pi}
                    />
                  )
                })
                .toArray()}
            <div className="rfd-ui-slider-handle" style={{ left: handleX }} key="h">
              <div className="rfd-ui-slider-handle-arrow" ref={this.saveRef('Arrow')} />
            </div>
          </div>
        </div>
        <div className="rfd-ui-slider-bottom-label">
          {(showLabel || (showLabelDragging && dragging)) && bottomText && (
            <div
              ref={this.saveRef('BLabel')}
              className={classNames('rfd-ui-slider-bottom-label-text', {
                'rfd-ui-slider-bottom-label-text-novalue': !hasValue
              })}
              dangerouslySetInnerHTML={{ __html: bottomText || NBSP }}
            />
          )}
        </div>
      </div>
    )
  }
}
